/**
 * Lazy loader for podcast player
 * Only loads the full React PodcastPlayer component when user clicks the play button
 * Replaces the ViewComponent trigger with the full interactive player
 */

import { trackClick } from 'js/utils/trackClick';

async function loadAndMountPodcastPlayer(
  mountElement: HTMLElement,
  playlistUrl: string,
): Promise<void> {
  // Dynamically import React and the podcast player
  const [{ default: React }, { createRoot }, { LoswpPodcastPlayer }] =
    await Promise.all([
      import('react'),
      import('react-dom/client'),
      import('./LoswpPodcastPlayer'),
    ]);

  // Clear any loading message before mounting
  mountElement.innerHTML = '';

  // Mount the full podcast player
  const root = createRoot(mountElement);

  root.render(
    React.createElement(LoswpPodcastPlayer, {
      playlistUrl,
    }),
  );
}

export function initializePodcastPlayers(): void {
  // Find all podcast episode components on the page
  const episodeElements = document.querySelectorAll('.podcast-episode');

  episodeElements.forEach((episodeEl) => {
    const trigger = episodeEl.querySelector('.js-podcast-trigger');
    const mountPoint = episodeEl.querySelector('.js-podcast-player-mount');

    if (!trigger || !mountPoint) return;

    // Get podcast data from window.loswp (set in show.html.slim)
    const playlistUrl = window.loswp?.podcast?.playlistUrl;
    const workId = window.loswp?.work?.id;

    if (!playlistUrl) {
      return;
    }

    // Add click handler to lazy load the full podcast player
    let playerLoaded = false;
    trigger.addEventListener('click', async (e) => {
      e.preventDefault();
      if (playerLoaded) return;
      playerLoaded = true;

      if (workId) {
        trackClick({
          source: 'loswp_podcast_play',
          entityId: workId,
        });
      }

      // Show loading state in mount point
      (mountPoint as HTMLElement).innerHTML =
        '<div class="podcast-loading">Loading podcast player...</div>';

      try {
        // Load and mount the full podcast player
        await loadAndMountPodcastPlayer(mountPoint as HTMLElement, playlistUrl);

        // Hide the trigger once player is loaded
        (trigger as HTMLElement).style.display = 'none';
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load podcast player:', error);
        (mountPoint as HTMLElement).innerHTML =
          '<div class="podcast-error">Failed to load podcast player. Please try again later.</div>';
        // Re-enable the trigger so user can try again
        playerLoaded = false;
      }
    });
  });
}
